import { Flex, Typography } from "antd";
import { UpcomingClassRow } from "hooks/queries/user";
import { ClassTypeBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { ClassCategory } from "shared/types/enum";
import { DEFAULT_DATE_FORMAT, localTzDate } from "shared/utils/dayjs";
import { textPx } from "shared/constants/zoom";

const { Text } = Typography;

export const upcomingRowStyle = {
  width: "100%",
  boxSizing: "border-box" as const,
  textAlign: "left" as const,
  padding: "10px 12px",
  border: `1px solid ${colors.line}`,
  borderRadius: 10,
  background: colors.card,
  cursor: "pointer",
  font: "inherit",
};

export const upcomingMetaStyle = { fontSize: textPx(12.5), color: colors.ink2 };

// The lists can run long (a studio with auto-complete off may have dozens of
// unmarked classes): they scroll inside the pop-up so its buttons stay put.
export const upcomingListStyle = {
  maxHeight: 264,
  overflowY: "auto" as const,
  paddingRight: 2,
};

// A known Monday, to name weekdays in the admin's language.
const MONDAY = "2026-09-28";
const weekdayName = (mondayFirst: number) =>
  localTzDate(MONDAY).add(mondayFirst, "day").format("dddd");

export const classDateTime = (iso: string) =>
  localTzDate(iso).format(`ddd, ${DEFAULT_DATE_FORMAT} · HH:mm`);

// "Setiap Senin, Rabu 08:00" for a series, the date and time for a single
// class. A pattern the calendar can't create (monthly, from old data) shows
// its first and last upcoming date instead.
const whenLabel = (row: UpcomingClassRow) => {
  if (!row.series) return classDateTime(row.first_occurrence);
  const { freq, weekdays, time } = row.series;
  if (freq === "daily") return translateWithVars("staff_status.series.daily", { time });
  if (freq === "weekly" && weekdays.length > 0) {
    return translateWithVars("staff_status.series.weekly", {
      days: weekdays.map(weekdayName).join(", "),
      time,
    });
  }
  const fmt = (iso: string) => localTzDate(iso).format(DEFAULT_DATE_FORMAT);
  return `${fmt(row.first_occurrence)} – ${fmt(row.last_occurrence)}`;
};

// NBSP-joined so a count never wraps away from its word.
export const keepTogether = (text: string) => text.replace(/ /g, " ");

const bookingsLabel = (row: UpcomingClassRow) =>
  row.booking_count > 0
    ? keepTogether(translateWithVars("staff_status.upcoming.bookings", { n: row.booking_count }))
    : null;

const countLabel = (row: UpcomingClassRow) =>
  keepTogether(translateWithVars("staff_status.upcoming.count", { n: row.class_count }));

/**
 * The upcoming classes that block a deactivation (one instructor's classes)
 * or a class delete (one class's sessions), one row per series, each opening
 * its class. `variant` picks what the row leads with: an instructor's list
 * names the class (with its type badge), a class's list names when it runs
 * and who teaches it, since the class is the same on every row.
 */
export const UpcomingClassList = ({
  rows,
  variant,
  onOpen,
}: {
  rows: UpcomingClassRow[];
  variant: "instructor" | "class";
  onOpen: (scheduleId: string, occurrence: string) => void;
}) => (
  <Flex vertical gap={8} style={upcomingListStyle}>
    {rows.map((row) => (
      <button
        key={`${row.schedule_id}_${row.first_occurrence}`}
        type="button"
        style={upcomingRowStyle}
        onClick={() => onOpen(row.schedule_id, row.first_occurrence)}
      >
        {variant === "instructor" ? (
          <Flex justify="space-between" align="center" gap={12}>
            <Flex vertical style={{ minWidth: 0 }}>
              <Text strong ellipsis>
                {row.class_name}
              </Text>
              {/* A series gets its counts on a line of their own; a
                  single class keeps its booking count beside the date. */}
              <Text style={upcomingMetaStyle}>
                {[whenLabel(row), !row.series && bookingsLabel(row)]
                  .filter(Boolean)
                  .join(" · ")}
              </Text>
              {row.series && (
                <Text style={upcomingMetaStyle}>
                  {[countLabel(row), bookingsLabel(row)].filter(Boolean).join(" · ")}
                </Text>
              )}
            </Flex>
            <ClassTypeBadge type={row.class_type as ClassCategory} />
          </Flex>
        ) : (
          <Flex vertical style={{ minWidth: 0 }}>
            <Text strong ellipsis>
              {whenLabel(row)}
            </Text>
            <Text style={upcomingMetaStyle}>
              {[row.instructor_name, row.series && countLabel(row), bookingsLabel(row)]
                .filter(Boolean)
                .join(" · ")}
            </Text>
          </Flex>
        )}
      </button>
    ))}
  </Flex>
);
